import { unwrap } from '../../../api/client';

/** `attachment; filename="sales.csv"` → `sales.csv` (falls back when missing or unsafe). */
export function filenameFromDisposition(
  header: string | null | undefined,
  fallback: string,
): string {
  const safeFallback = ensureCsv(sanitize(fallback) || 'report');
  if (!header) return safeFallback;
  const star = /filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i.exec(header);
  const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
  let name = '';
  if (star?.[1]) {
    try {
      name = decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''));
    } catch {
      name = '';
    }
  }
  if (!name && plain?.[1]) name = plain[1].trim();
  const clean = sanitize(name);
  return clean ? ensureCsv(clean) : safeFallback;
}

function sanitize(name: string): string {
  // Strip path parts and characters that are invalid in file names.
  return (name.split(/[\\/]/).pop() ?? '').replace(/[<>:"|?*]/g, '').trim();
}

function ensureCsv(name: string): string {
  return /\.csv$/i.test(name) ? name : `${name}.csv`;
}

/** A file name for a report export: `sales-by-period-2026-10-01-to-2026-10-20.csv`. */
export function reportFileName(
  slug: string,
  range?: { from: string; to: string } | string,
): string {
  if (!range) return `${slug}.csv`;
  if (typeof range === 'string') return `${slug}-${range}.csv`;
  return `${slug}-${range.from}-to-${range.to}.csv`;
}

/** Saves text as a downloaded file (a UTF-8 BOM keeps Excel happy with non-ASCII names). */
export function downloadText(
  text: string,
  filename: string,
  type = 'text/csv;charset=utf-8',
): void {
  const BOM = '\uFEFF';
  const body = text.startsWith(BOM) ? text : `${BOM}${text}`;
  const url = URL.createObjectURL(new Blob([body], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Downloads the CSV form of an endpoint. Pass the openapi-fetch call made with `format: 'csv'` and
 * `parseAs: 'text'`; the server's file name is used when it sends one.
 */
export async function downloadCsv(
  call: Promise<{ data?: unknown; error?: unknown; response: Response }>,
  fallbackName: string,
): Promise<string> {
  const result = await call;
  const data = await unwrap(Promise.resolve(result));
  const text = typeof data === 'string' ? data : JSON.stringify(data);
  const filename = filenameFromDisposition(
    result.response.headers.get('content-disposition'),
    fallbackName,
  );
  downloadText(text, filename);
  return filename;
}

/** A report's CSV export, fetched but not saved: the text and the server's file name. */
export interface CsvFile {
  text: string;
  filename: string;
}

/**
 * Fetches the CSV form of a report endpoint (the openapi-fetch call made with `format: 'csv'` and
 * `parseAs: 'text'`). The report frame turns it into an Excel file, a CSV download or a printout.
 */
export async function fetchCsv(
  call: Promise<{ data?: unknown; error?: unknown; response: Response }>,
  fallbackName: string,
): Promise<CsvFile> {
  const result = await call;
  const data = await unwrap(Promise.resolve(result));
  return {
    text: typeof data === 'string' ? data : JSON.stringify(data),
    filename: filenameFromDisposition(
      result.response.headers.get('content-disposition'),
      fallbackName,
    ),
  };
}
